import React from "react"
import { DbgPaths } from "#/utils/Paths"
import { getMedicationCategorySections } from "./multiple-categories/MedicationsCategory"
import { getQmeCategorySections } from "./multiple-categories/QmeCategory"
import useBenefit from "../../../dynamic-content/hooks/useGetBenefit"
import { FinancialSupportErrorMessage } from "./FinancialSupportErrorMessage"
import {
  Box,
  CloseIcon,
  Dialog,
  DialogTitle,
  IconButton,
  Link,
  Progress,
  Stack,
  Typography,
  UnorderedList,
  UnorderedListItem
} from "@carrotfertility/carotene-core"
import { FormattedMessage, useIntl } from "react-intl"
import { useSelector } from "react-redux"
import { useHistory } from "react-router"
import EmployeeOnlyModalContainer from "../../../employee-only-containers/EmployeeOnlyModalContainer"
import { getCategoryName } from "../ReimbursementCategoryMessages"
import { CategoryCoverage, getCategoryCoverage } from "#/utils/CategoryCoverage"
import { getSubsidies } from "#/redux/reducers/subsidies"
import { getPreservationSections } from "./multiple-categories/PreservationCategory"
import { getFertilityAndPreservationSections } from "./multiple-categories/FertilityAndPreservationCategory"
import { getFertilitySections } from "./multiple-categories/FertilityCategory"
import {
  getOtherExpensesSections,
  OtherExpensesCardCopy,
  otherCardHasList
} from "./multiple-categories/OtherExpensesCategory"
import { getMenopauseLowTestosteroneSections } from "./multiple-categories/MenopauseLowTestosteroneCategory"
import { getAdoptionAndGCSections } from "./multiple-categories/AdoptionAndGCCategory"
import { reimbursementCategoryNameMessageKeys } from "../reimbursementCategoryNameMessageKeys"
import { TileAccordion } from "#/components/views/molecules/TileAccordion"
import { Subsidy } from "#/lib/carrot-api/types/Subsidy"

interface FormattedSubsidy extends Subsidy {
  categoryName: string
  formattedYearlyBenefit: string
  formattedLifetimeBenefit: string
}

function coverageLink(href: string) {
  return function CoverageLink(linkContent: React.ReactNode) {
    return (
      <Link target="_blank" href={href}>
        {linkContent}
      </Link>
    )
  }
}

export const SingleJourneyDescription = ({ category }: { category: string }): JSX.Element | null => {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.FERTILITY:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>fertility expenses</link>."
          values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.PRESERVATION:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>fertility preservation expenses</link>."
          values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.ADOPTION:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>adoption expenses</link>."
          values={{ link: coverageLink(DbgPaths.ADOPTION_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.GC:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>gestational surrogacy expenses</link>."
          values={{ link: coverageLink(DbgPaths.GESTATIONAL_SURROGACY_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.PREGNANCY:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>pregnancy and postpartum expenses</link>."
          values={{ link: coverageLink(DbgPaths.PREGNANCY_COVERAGE) }}
        />
      )
    case reimbursementCategoryNameMessageKeys.GAC:
      return (
        <FormattedMessage
          defaultMessage="This category includes eligible <link>gender-affirming care expenses</link>."
          values={{ link: coverageLink(DbgPaths.GENDER_AFFIRMING_CARE) }}
        />
      )
    default:
      return null
  }
}

// Card-only copy; null falls back to the shared sections body.
function getCoveredExpensesCardCopy(
  category: string,
  coverage: CategoryCoverage,
  singleCategory = false
): React.ReactNode[] | null {
  // Members who see one category get "Your benefit covers"; only the fertility categories have that copy so far.
  if (singleCategory) return getSingleCategoryCardCopy(category, coverage)
  switch (category) {
    case reimbursementCategoryNameMessageKeys.QME:
      return [
        <FormattedMessage
          key="qme"
          defaultMessage="This category includes eligible <link>qualified medical expenses</link>."
          values={{ link: coverageLink(DbgPaths.QUALIFIED_MEDICAL_EXPENSES) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.ADOPTION:
      return [
        <FormattedMessage
          key="adoption"
          defaultMessage="This category includes eligible <link>adoption</link> expenses."
          values={{ link: coverageLink(DbgPaths.ADOPTION_COVERAGE) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.FERTILITY:
      return [
        <FormattedMessage
          key="fertility"
          defaultMessage="This category includes eligible <link>fertility care</link> expenses."
          values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
        />,
        coverage.preservation && (
          <FormattedMessage
            key="fertility-definition"
            defaultMessage="Fertility care involves actively trying for a pregnancy, including up to one year of related storage costs for eggs, sperm, and embryos."
          />
        )
      ]
    case reimbursementCategoryNameMessageKeys.PRESERVATION:
      return [
        <FormattedMessage
          key="preservation"
          defaultMessage="This category includes eligible <link>fertility preservation</link> expenses."
          values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
        />,
        coverage.fertility && (
          <FormattedMessage
            key="preservation-definition"
            defaultMessage="Fertility preservation involves freezing eggs, sperm, or embryos for future use. This means you do not intend to use the material (thaw, inseminate, transfer, etc.) for at least one year after freezing it."
          />
        )
      ]
    case reimbursementCategoryNameMessageKeys.GAC:
      return [
        <FormattedMessage
          key="gac"
          defaultMessage="This category includes eligible <link>gender-affirming care</link> expenses."
          values={{ link: coverageLink(DbgPaths.GENDER_AFFIRMING_CARE) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.GC:
      return [
        <FormattedMessage
          key="gc"
          defaultMessage="This category includes eligible <link>gestational surrogacy</link> expenses."
          values={{ link: coverageLink(DbgPaths.GESTATIONAL_SURROGACY_COVERAGE) }}
        />
      ]
    case reimbursementCategoryNameMessageKeys.PREGNANCY:
      return [
        <FormattedMessage
          key="pregnancy"
          defaultMessage="This category includes eligible <link>pregnancy and postpartum</link> expenses."
          values={{ link: coverageLink(DbgPaths.PREGNANCY_COVERAGE) }}
        />
      ]
    default:
      return null
  }
}

function getSingleCategoryCardCopy(category: string, coverage: CategoryCoverage): React.ReactNode[] | null {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.FERTILITY:
      return [
        <FormattedMessage
          key="fertility"
          defaultMessage="Your benefit covers eligible <link>fertility care</link> expenses."
          values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
        />,
        coverage.preservation && (
          <FormattedMessage
            key="fertility-definition"
            defaultMessage="Fertility care involves actively trying for a pregnancy, including up to one year of related storage costs for eggs, sperm, and embryos."
          />
        )
      ]
    case reimbursementCategoryNameMessageKeys.PRESERVATION:
      return [
        <FormattedMessage
          key="preservation"
          defaultMessage="Your benefit covers eligible <link>fertility preservation</link> expenses."
          values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
        />,
        coverage.fertility && (
          <FormattedMessage
            key="preservation-definition"
            defaultMessage="Fertility preservation involves freezing eggs, sperm, or embryos for future use. This means you do not intend to use the material (thaw, inseminate, transfer, etc.) for at least one year after freezing it."
          />
        )
      ]
    case reimbursementCategoryNameMessageKeys.FERTILITY_AND_PRESERVATION:
      return [
        <FormattedMessage
          key="journeys"
          defaultMessage="Your benefit covers eligible expenses for the following journeys:"
        />,
        <UnorderedList key="journey-list">
          <UnorderedListItem>
            <FormattedMessage
              defaultMessage="<link>Fertility care</link>"
              values={{ link: coverageLink(DbgPaths.FERTILITY_COVERAGE) }}
            />
          </UnorderedListItem>
          <UnorderedListItem>
            <FormattedMessage
              defaultMessage="<link>Fertility preservation</link>"
              values={{ link: coverageLink(DbgPaths.PRESERVATION_COVERAGE) }}
            />
          </UnorderedListItem>
        </UnorderedList>
      ]
    default:
      return null
  }
}

// Body shared by the Learn modal and the Covered expenses card.
const CategorySectionsBody = ({
  category,
  hasQmeCategory
}: {
  category: string
  hasQmeCategory: boolean
}): JSX.Element => {
  const sections = getCategorySections(category, hasQmeCategory)
  return (
    <Box>
      <Typography>{sections.header}</Typography>
      {(sections.listItems || sections.footer) && (
        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
          {sections.listItems && <UnorderedList>{sections.listItems}</UnorderedList>}
          {sections.footer && (
            <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
              <Typography>{sections.footer}</Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

const CoveredExpensesContent = ({
  category,
  coverage,
  singleCategory
}: {
  category: string
  coverage: CategoryCoverage
  singleCategory: boolean
}): JSX.Element => {
  const { data: benefit, isLoading } = useBenefit()

  const cardCopy = getCoveredExpensesCardCopy(category, coverage, singleCategory)?.filter(Boolean)
  if (cardCopy) {
    return (
      <Stack sx={(theme) => ({ gap: theme.tokens.spacing.xs })}>
        {cardCopy.map((paragraph, i) =>
          React.isValidElement(paragraph) && paragraph.type === UnorderedList ? (
            paragraph
          ) : (
            <Typography key={i}>{paragraph}</Typography>
          )
        )}
      </Stack>
    )
  }
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <Progress />
      </Box>
    )
  }
  if (benefit == null) {
    return <FinancialSupportErrorMessage />
  }
  if (category === reimbursementCategoryNameMessageKeys.OTHER) {
    return <OtherExpensesCardCopy coverage={coverage} benefit={benefit} />
  }

  return <CategorySectionsBody category={category} hasQmeCategory={coverage.qme} />
}

export function CoveredExpensesCard({
  category: categoryKey,
  coverage,
  singleCategory = false
}: {
  category: string
  coverage: CategoryCoverage
  singleCategory?: boolean
}): JSX.Element {
  const { data: benefit } = useBenefit()
  // Single-category fund members have DEFAULT, which would otherwise fall back to the old Learn modal sections.
  const category =
    categoryKey === reimbursementCategoryNameMessageKeys.DEFAULT
      ? reimbursementCategoryNameMessageKeys.OTHER
      : categoryKey
  const cardCopy = getCoveredExpensesCardCopy(category, coverage, singleCategory)?.filter(Boolean)
  // "This category" reads oddly when the member only has one.
  if (singleCategory && !cardCopy) {
    return (
      <TileAccordion
        id={`covered-expenses-${category}`}
        color="blue"
        title={<FormattedMessage defaultMessage="Covered expenses" />}
        collapsible={false}
      >
        <Typography>
          <FormattedMessage
            defaultMessage="<link>Learn more about your coverage</link>."
            values={{ link: coverageLink(DbgPaths.CARROT_ELIGIBLE) }}
          />
        </Typography>
      </TileAccordion>
    )
  }
  // One sentence: no caret.
  const collapsible = cardCopy
    ? cardCopy.length > 1
    : category !== reimbursementCategoryNameMessageKeys.OTHER || benefit == null || otherCardHasList(coverage, benefit)
  return (
    <TileAccordion
      id={`covered-expenses-${category}`}
      color="blue"
      title={<FormattedMessage defaultMessage="Covered expenses" />}
      collapsible={collapsible}
    >
      <CoveredExpensesContent category={category} coverage={coverage} singleCategory={singleCategory} />
    </TileAccordion>
  )
}

// @ts-expect-error TS(7006) FIXME: Parameter 'props' implicitly has an 'any' type.
const PayingForCareContentMain = (props): JSX.Element => {
  const { category, hasQmeCategory } = props
  const { data: benefit, isLoading } = useBenefit()
  const coverage = getCategoryCoverage(useSelector(getSubsidies))
  const intl = useIntl()

  let body: JSX.Element
  if (isLoading) {
    body = (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Progress />
      </Box>
    )
  } else if (benefit == null) {
    body = <FinancialSupportErrorMessage />
  } else {
    body = <CategorySectionsBody category={category} hasQmeCategory={hasQmeCategory} />
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={(theme) => ({ padding: "none", marginBlockEnd: theme.tokens.spacing.xl })}>
        <DialogTitle id="fund-category-name">{intl.formatMessage(getCategoryName(category, coverage.qme))}</DialogTitle>
        <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.md })}>{body}</Box>
      </Box>
    </Box>
  )
}

export function PayingForCareCoverageModal({
  subsidyCategory,
  hasQmeCategory
}: {
  subsidyCategory: FormattedSubsidy
  hasQmeCategory: boolean
}): JSX.Element {
  const [showModal, setShowModal] = React.useState(false)
  const intl = useIntl()

  return (
    <>
      <CategoryLearnLink
        category={subsidyCategory.reimbursementCategoryNameMessageKey}
        handleOpen={() => setShowModal(true)}
      />
      <EmployeeOnlyModalContainer onClose={() => setShowModal(false)}>
        <Dialog
          aria-labelledby="fund-category-name"
          onClose={(event: React.MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation()
            setShowModal(false)
          }}
          open={showModal}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end"
            }}
          >
            <IconButton
              aria-label={intl.formatMessage({ defaultMessage: "close" })}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                setShowModal(false)
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <PayingForCareContentMain
            category={subsidyCategory.reimbursementCategoryNameMessageKey}
            hasQmeCategory={hasQmeCategory}
          />
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xxxl })}></Box>
        </Dialog>
      </EmployeeOnlyModalContainer>
    </>
  )
}

function CategoryLearnLink({ category, handleOpen }: { category: string; handleOpen: () => void }): React.JSX.Element {
  const { data: benefit } = useBenefit()
  const history = useHistory()

  let href: string = null
  switch (category) {
    case reimbursementCategoryNameMessageKeys.ADOPTION: {
      href = DbgPaths.ADOPTION_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.GC: {
      href = DbgPaths.GESTATIONAL_SURROGACY_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY: {
      if (
        !benefit?.journeys.fertilityCare.eligibleExpensesDetail.isElectiveCareCovered &&
        !benefit?.journeys.fertilityCare.eligibleExpensesDetail.isCoveredWithInfertilityDiagnosis
      ) {
        href = DbgPaths.FERTILITY_COVERAGE
      }
      break
    }
    case reimbursementCategoryNameMessageKeys.GAC: {
      href = DbgPaths.GENDER_AFFIRMING_CARE
      break
    }
    case reimbursementCategoryNameMessageKeys.PREGNANCY: {
      href = DbgPaths.PREGNANCY_COVERAGE
      break
    }
    case reimbursementCategoryNameMessageKeys.PRESERVATION: {
      if (
        benefit?.journeys.fertilityPreservation.eligibleExpensesDetail.isElectivePreservationCovered ||
        benefit?.journeys.fertilityPreservation.eligibleExpensesDetail.isMedicallyNecessaryPreservationCovered ||
        benefit?.journeys.fertilityCare.eligibleExpensesDetail.hasDonorAssistance
      ) {
        href = DbgPaths.PRESERVATION_COVERAGE
      }
      break
    }
  }

  return (
    <Typography
      variant="body1"
      color={(theme) => theme.palette.text.secondary}
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: (theme) => theme.spacing(theme.tokens.spacing.xxs)
      }}
    >
      <FormattedMessage
        defaultMessage="<link>Learn</link> what this includes"
        values={{
          link: (linkContent) => (
            <Link
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                if (href) {
                  history.push(href)
                }
                handleOpen()
              }}
              color="inherit"
              component="button"
              sx={{
                pointerEvents: "auto"
              }}
            >
              {linkContent}
            </Link>
          )
        }}
      />
    </Typography>
  )
}

export interface CategorySections {
  header: React.JSX.Element
  listItems?: React.JSX.Element
  footer?: React.JSX.Element
}

export function getCategorySections(category: string, hasQmeCategory: boolean): CategorySections {
  switch (category) {
    case reimbursementCategoryNameMessageKeys.FERTILITY_GC_ADOPTION:
    case reimbursementCategoryNameMessageKeys.ADOPTION_AND_GC: {
      return getAdoptionAndGCSections(category)
    }
    case reimbursementCategoryNameMessageKeys.MEDICATION: {
      return getMedicationCategorySections()
    }
    case reimbursementCategoryNameMessageKeys.QME: {
      return getQmeCategorySections()
    }
    case reimbursementCategoryNameMessageKeys.M_LT:
    case reimbursementCategoryNameMessageKeys.M_LT_GAC:
    case reimbursementCategoryNameMessageKeys.PREGNANCY_M_LT:
    case reimbursementCategoryNameMessageKeys.FERTILITY_M_LT: {
      return getMenopauseLowTestosteroneSections(category)
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY: {
      return getFertilitySections()
    }
    case reimbursementCategoryNameMessageKeys.FERTILITY_GC_ADOPTION:
    case reimbursementCategoryNameMessageKeys.FERTILITY_AND_PRESERVATION: {
      return getFertilityAndPreservationSections()
    }
    case reimbursementCategoryNameMessageKeys.PRESERVATION: {
      return getPreservationSections()
    }
  }
  return getOtherExpensesSections(hasQmeCategory)
}
